import { Application, ApplicationWithId } from "../interfaces/IApplications";
import { AppUser } from "../interfaces/IUsers";
import { Category } from "../interfaces/ICategories";
import { Contest, ContestWithId } from "../interfaces/IContests";
import { db } from "../App";
import { Group, GroupWithId } from "../interfaces/IGroups";
import { isRegistrationOpen } from "./utils";
import { LogData, LogEntry } from "../interfaces/ILogs";
import { Participant, ParticipantWithId } from "../interfaces/IParticipants";
import { Presentation, PresentationWithId } from "../interfaces/IPresentations";
import {
  collection as col,
  collectionGroup,
  deleteDoc,
  doc,
  DocumentData,
  getDoc,
  getDocs,
  getFirestore,
  onSnapshot as firestoreOnSnapshot,
  orderBy,
  query,
  Query,
  QuerySnapshot,
  setDoc,
  Timestamp,
  runTransaction,
  serverTimestamp,
  Unsubscribe,
  updateDoc,
  where,
  WhereFilterOp,
  WithFieldValue,
  WriteBatch,
  writeBatch,
} from "firebase/firestore";

// ----------------------------------------------------- //
// --------------- TYPY I INTERFEJSY ------------------- //
// ----------------------------------------------------- //

type NewDocTypes =
  | Application
  | AppUser
  | Category
  | Contest
  | Group
  | Participant
  | Presentation
  | LogData;

export interface WhereCondition {
  field: string;
  operator: WhereFilterOp;
  value: any;
}

export interface OrderByCondition {
  field: string;
  direction: "asc" | "desc";
}

// ----------------------------------------------------- //
// ----------------- FUNKCJE LOKALNE ------------------- //
// ----------------------------------------------------- //

const BATCH_LIMIT = 500;

const batchOperation = async (operations: ((batch: WriteBatch) => void)[]) => {
  const batches: WriteBatch[] = [writeBatch(db)];
  let currentBatchIndex = 0;
  let operationsCount = 0;

  operations.forEach((operation) => {
    if (operationsCount === BATCH_LIMIT) {
      batches.push(writeBatch(db));
      currentBatchIndex++;
      operationsCount = 0;
    }
    operation(batches[currentBatchIndex]);
    operationsCount++;
  });

  try {
    await Promise.all(batches.map((batch) => batch.commit()));
    console.warn("Wszystkie operacje wsadowe zostały pomyślnie ukończone.");
  } catch (error) {
    console.error("Wystąpił błąd podczas wykonywania operacji wsadowych: ", error);
  }
};

const buildQueryForCollectionGroup = (
  whereConditions: WhereCondition[],
  orderByConditions: OrderByCondition[],
  collectionGroupName: string,
) => {
  const ref = collectionGroup(db, collectionGroupName as string);
  let q: Query = query(ref);
  whereConditions.forEach(({ field, operator, value }) => {
    q = query(q, where(field, operator, value));
  });
  orderByConditions.forEach(({ field, direction }) => {
    q = query(q, orderBy(field, direction));
  });
  return q;
};

async function addDocumentsBatch<T extends WithFieldValue<DocumentData>>(
  collectionPath: string,
  documents: T[],
  setIds?: boolean,
): Promise<void> {
  const collectionRef = col(db, collectionPath);

  const operations = documents.map((docData) => (batch: WriteBatch) => {
    const docRef = setIds ? doc(collectionRef, docData.id) : doc(collectionRef);
    batch.set(docRef, convertDatesToTimestamps(docData));
  });

  await batchOperation(operations);
}

const convertDatesToTimestamps = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map((item) => convertDatesToTimestamps(item));
  } else if (typeof obj === "object" && obj !== null) {
    const result: any = {};
    for (const key in obj) {
      if (obj[key] instanceof Date) {
        result[key] = Timestamp.fromDate(obj[key] as Date);
      } else if (obj[key] === null || obj[key] === "") {
        // Zapisywanie null dla przedziałów otwartych dat
        result[key] = null;
      } else if (typeof obj[key] === "object" && obj[key] !== null) {
        result[key] = convertDatesToTimestamps(obj[key]);
      } else {
        result[key] = obj[key];
      }
    }
    return result;
  } else {
    return obj;
  }
};

const convertTimestampsToDates = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map((item) => convertTimestampsToDates(item));
  } else if (typeof obj === "object" && obj !== null) {
    const result: any = {};
    for (const key in obj) {
      if (obj[key] instanceof Timestamp) {
        result[key] = obj[key].toDate();
      } else {
        result[key] = convertTimestampsToDates(obj[key]);
      }
    }
    return result;
  } else {
    return obj;
  }
};

const crudOperation = async <T extends NewDocTypes>(
  operation: "create" | "update" | "delete",
  collectionPath: string,
  data?: T,
  id?: string,
): Promise<any> => {
  const colRef = col(db, collectionPath);

  switch (operation) {
    case "create":
      const docRef = doc(colRef);
      const newDocWithId = { ...data, id: docRef.id } as T;
      await setDoc(docRef, convertDatesToTimestamps(newDocWithId));
      return newDocWithId;
    case "update":
      if (!id) throw new Error("ID is required for update operation");
      const convertedData = convertDatesToTimestamps(data);
      await updateDoc(doc(colRef, id), convertedData);
      return null;
    case "delete":
      if (!id) throw new Error("ID is required for delete operation");
      await deleteDoc(doc(colRef, id));
      return null;
  }
};

const deleteDocumentWithSubcollections = async (
  collectionPath: string,
  docId: string,
  subcollections: string[],
) => {
  const firestore = db;
  const docRef = doc(firestore, collectionPath, docId);
  const operations: ((batch: WriteBatch) => void)[] = [];
  for (const subcollection of subcollections) {
    const subcollectionRef = col(firestore, `${collectionPath}/${docId}/${subcollection}`);
    const subcollectionSnapshot = await getDocs(query(subcollectionRef));
    subcollectionSnapshot.forEach((subDoc) => {
      const subDocRef = doc(firestore, `${collectionPath}/${docId}/${subcollection}`, subDoc.id);
      operations.push((batch) => batch.delete(subDocRef));
    });
  }
  // Dodajemy operację usunięcia głównego dokumentu
  operations.push((batch) => batch.delete(docRef));
  await batchOperation(operations);
};

const find = async (
  type: "collection" | "collectionGroup",
  path: string,
  whereConditions: WhereCondition[] = [],
  orderByConditions: OrderByCondition[] = [],
): Promise<any[]> => {
  let q: Query;

  if (type === "collection") {
    q = query(col(db, path));
  } else {
    q = query(collectionGroup(db, path));
  }

  whereConditions.forEach(({ field, operator, value }) => {
    q = query(q, where(field, operator, value));
  });

  orderByConditions.forEach(({ field, direction }) => {
    q = query(q, orderBy(field, direction));
  });

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => convertTimestampsToDates({ id: doc.id, ...doc.data() }));
};

/**
 * Generyczna funkcja do subskrypcji zmian w kolekcji Firestore (onSnapshot)
 * @param type - Typ kolekcji: "collection" lub "collectionGroup"
 * @param path - Ścieżka do kolekcji
 * @param callback - Funkcja wywoływana przy każdej zmianie danych
 * @param whereConditions - Warunki filtrowania (opcjonalne)
 * @param orderByConditions - Warunki sortowania (opcjonalne)
 * @returns Funkcja unsubscribe do odsubskrybowania
 */
const onSnapshot = (
  type: "collection" | "collectionGroup",
  path: string,
  callback: (data: any[]) => void,
  whereConditions: WhereCondition[] = [],
  orderByConditions: OrderByCondition[] = [],
): Unsubscribe => {
  let q: Query;

  if (type === "collection") {
    q = query(col(db, path));
  } else {
    q = query(collectionGroup(db, path));
  }

  whereConditions.forEach(({ field, operator, value }) => {
    q = query(q, where(field, operator, value));
  });

  orderByConditions.forEach(({ field, direction }) => {
    q = query(q, orderBy(field, direction));
  });

  return firestoreOnSnapshot(
    q,
    (snapshot: QuerySnapshot<DocumentData>) => {
      const data = snapshot.docs.map((doc) =>
        convertTimestampsToDates({ id: doc.id, ...doc.data() })
      );
      callback(data);
    },
    (error) => {
      console.error(`Błąd podczas subskrypcji ${path}:`, error);
      callback([]); // Zwróć pustą tablicę w przypadku błędu
    }
  );
};

const findById = async (collection: string, id: string): Promise<any> => {
  try {
    const ref = doc(db, collection, id);
    const snapshot = await getDoc(ref);
    const obj = { id: snapshot.id, ...snapshot.data() };
    return convertTimestampsToDates(obj);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export async function findByQuery(query: Query<DocumentData>): Promise<any[]> {
  try {
    const snapshot: QuerySnapshot<DocumentData> = await getDocs(query);
    const data = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    return convertTimestampsToDates(data);
  } catch (error) {
    console.error("Error fetching documents: ", error);
    throw error;
  }
}

const onSetDoc = (collection: string, docId: string, document: AppUser) => {
  const colRef = col(db, "/" + collection);
  const docRef = doc(colRef, docId);
  setDoc(docRef, document).catch(console.error);
};

const updateParticipantPresentationsMap = (
  batch: ReturnType<typeof writeBatch>, // typ dla batcha
  db: ReturnType<typeof getFirestore>, // typ dla firestore
  userId: string,
  application: ApplicationWithId,
) => {
  // Funkcja ściąga z lokalnej wersji aplikacji listę participantów i wrzuca do bazy (updatedApplicationData)
  const appColRef = col(db, "users/" + userId + "/applications");
  const applicationRef = doc(appColRef, application.id);
  batch.update(applicationRef, convertDatesToTimestamps(application));
};

// ----------------------------------------------------- //
// --------------- FUNKCJE EKSPORTOWANE ---------------- //
// ----------------------------------------------------- //

export const createApplication = async (application: Application, userId: string) => {
  return await crudOperation("create", "users/" + userId + "/applications", application);
};

export const createParticipant = async (participant: Participant, userId: string) => {
  return await crudOperation("create", "users/" + userId + "/participants", participant);
};

export const createParticipants = async (participants: Participant[], userId: string) => {
  return await addDocumentsBatch("users/" + userId + "/participants", participants);
};

export const createPresentationAndUpdateParticipantsCounter = async (
  presentation: Presentation,
  userId: string,
  application: ApplicationWithId,
): Promise<Presentation | null> => {
  let createdPresentation: PresentationWithId | null = null;

  try {
    const operations = [
      (batch: WriteBatch) => {
        const presColRef = col(db, `users/${userId}/applications/${application.id}/presentations`);
        const presentationRef = doc(presColRef);
        createdPresentation = { ...presentation, id: presentationRef.id };
        batch.set(presentationRef, createdPresentation);
      },
      (batch: WriteBatch) => {
        updateParticipantPresentationsMap(batch, db, userId, application);
      },
    ];

    await batchOperation(operations);
    return createdPresentation;
  } catch (error) {
    console.error("Błąd podczas operacji dodawania prezentacji w batchu:", error);
    return null;
  }
};

export const deleteApplication = (applicationId: string, userId: string) => {
  const collectionPath = "users/" + userId + "/applications";
  const subcollections = ["presentations"];
  return deleteDocumentWithSubcollections(collectionPath, applicationId, subcollections);
};

export const deletePresentationAndUpdateParticipantsCounter = async (
  presentation: PresentationWithId, // Obiekt prezentacji, który usuwamy
  userId: string,
  application: ApplicationWithId,
) => {
  const operations = [
    (batch: WriteBatch) => {
      const presColRef = col(db, `users/${userId}/applications/${application.id}/presentations`);
      const presentationRef = doc(presColRef, presentation.id);
      batch.delete(presentationRef);
    },
    (batch: WriteBatch) => {
      updateParticipantPresentationsMap(batch, db, userId, application);
    },
  ];

  await batchOperation(operations);
};

export const deleteParticipant = async (participantId: string, userId: string) => {
  return await crudOperation(
    "delete",
    "users/" + userId + "/participants",
    undefined,
    participantId,
  );
};

export const findApplication = async (userId: string, applicationId: string) => {
  return await findById("users/" + userId + "/applications", applicationId);
};

/**
 * Subskrybuje zmiany w pojedynczym dokumencie zgłoszenia (onSnapshot)
 * @param userId - ID użytkownika
 * @param applicationId - ID zgłoszenia
 * @param callback - Funkcja wywoływana przy każdej zmianie danych zgłoszenia
 * @returns Funkcja unsubscribe do odsubskrybowania
 */
export const findApplicationSnapshot = (
  userId: string,
  applicationId: string,
  callback: (application: ApplicationWithId | null) => void
): Unsubscribe => {
  const applicationRef = doc(db, `users/${userId}/applications`, applicationId);
  
  return firestoreOnSnapshot(
    applicationRef,
    (snapshot) => {
      if (snapshot.exists()) {
        const data = convertTimestampsToDates({
          id: snapshot.id,
          ...snapshot.data(),
        });
        callback(data as ApplicationWithId);
      } else {
        callback(null);
      }
    },
    (error) => {
      console.error(`Błąd podczas subskrypcji zgłoszenia ${applicationId}:`, error);
      callback(null);
    }
  );
};

// export const findAllApplications = async () => {
//   return await find("collectionGroup", "applications");
// };

// export const findAllApplicationsByQuery = async (
//   whereConditions: WhereCondition[],
//   orderByConditions: OrderByCondition[]
// ) => {
//   return await find("collectionGroup", "applications", whereConditions, orderByConditions);
// };

export const findUserApplications = async (userId: string) => {
  return await find("collection", `users/${userId}/applications`);
};

/**
 * Subskrybuje zmiany w kolekcji zgłoszeń użytkownika (onSnapshot)
 * @param userId - ID użytkownika
 * @param callback - Funkcja wywoływana przy każdej zmianie danych zgłoszeń
 * @returns Funkcja unsubscribe do odsubskrybowania
 */
export const findApplicationsSnapshot = (
  userId: string,
  callback: (applications: ApplicationWithId[]) => void
): Unsubscribe => {
  return onSnapshot(
    "collection",
    `users/${userId}/applications`,
    callback,
    [], // bez warunków where
    [] // bez sortowania
  );
};

export const findCategories = async (contestId: string) => {
  return await find("collection", `contests/${contestId}/categories`);
};

export const findCategoriesByQuery = async (
  collectionPath: string,
  whereConditions: WhereCondition[],
  orderByConditions: OrderByCondition[],
) => {
  return await find("collection", collectionPath, whereConditions, orderByConditions);
};

export const findAllPresentationsByQuery = async (
  whereConditions: WhereCondition[],
  orderByConditions: OrderByCondition[],
) => {
  return await find("collectionGroup", "presentations", whereConditions, orderByConditions);
};

export const findCategoryById = async (contestId: string, categoryId: string) =>
  await findById("contests/" + contestId + "/categories", categoryId);

export const findContests = async () => {
  return await find(
    "collection",
    "contests",
    [{ field: "inModeration", operator: "!=", value: true }],
    [{ field: "startDate", direction: "asc" }],
  );
};

/**
 * Subskrybuje zmiany w kolekcji turniejów (onSnapshot)
 * @param callback - Funkcja wywoływana przy każdej zmianie danych turniejów
 * @returns Funkcja unsubscribe do odsubskrybowania
 */
export const findContestsSnapshot = (
  callback: (contests: ContestWithId[]) => void
): Unsubscribe => {
  return onSnapshot(
    "collection",
    "contests",
    callback,
    [{ field: "inModeration", operator: "!=", value: true }],
    [{ field: "startDate", direction: "asc" }],
  );
};

/**
 * Subskrybuje zmiany w pojedynczym dokumencie turnieju (onSnapshot)
 * @param contestId - ID turnieju
 * @param callback - Funkcja wywoływana przy każdej zmianie danych turnieju
 * @returns Funkcja unsubscribe do odsubskrybowania
 */
export const findContestSnapshot = (
  contestId: string,
  callback: (contest: ContestWithId | null) => void
): Unsubscribe => {
  const contestRef = doc(db, "contests", contestId);
  
  return firestoreOnSnapshot(
    contestRef,
    (snapshot) => {
      if (snapshot.exists()) {
        const data = convertTimestampsToDates({
          id: snapshot.id,
          ...snapshot.data(),
        });
        callback(data as ContestWithId);
      } else {
        callback(null);
      }
    },
    (error) => {
      console.error(`Błąd podczas subskrypcji turnieju ${contestId}:`, error);
      callback(null);
    }
  );
};

export const findContestById = async (id: string) => await findById("contests", id);

export const findParticipantById = async (userId: string, participantId: string) =>
  await findById("users/" + userId + "/participants", participantId);

export const findParticipants = async (userId: string) => {
  return await find(
    "collection",
    `users/${userId}/participants`,
    [{ field: "isActive", operator: "==", value: true }],
    [
      { field: "lastName", direction: "asc" },
      { field: "firstName", direction: "asc" },
    ],
  );
};

export const findPresentation = async (
  userId: string,
  applicationId: string,
  presentationId: string,
) =>
  await findById(
    "users/" + userId + "/applications/" + applicationId + "/presentations",
    presentationId,
  );

export const findPresentations = async (userId: string, applicationId: string) => {
  return await find("collection", `users/${userId}/applications/${applicationId}/presentations`);
};

export const findPresentationsByQuery = async (
  collectionPath: string,
  whereConditions: WhereCondition[],
  orderByConditions: OrderByCondition[],
) => {
  return await find("collection", collectionPath, whereConditions, orderByConditions);
};

export const findUserById = async (id: string) => {
  return await findById("users", id);
};

export const findUserByEmail = async (email: string) => {
  const users = await find(
    "collection",
    "users",
    [{ field: "email", operator: "==", value: email.toLowerCase() }],
    [],
  );
  return users.length > 0 ? users[0] : null;
};

// export const findUsers = async () => {
//   return await find("collection", "users");
// };

export const onSetUser = (user: AppUser) => {
  onSetDoc("users", user.id, user);
};

export const updateApplication = async (application: ApplicationWithId) =>
  await crudOperation(
    "update",
    "users/" + application.userId + "/applications",
    application,
    application.id,
  );

// export const updateCategory = async (category: CategoryWithId) =>
//   await crudOperation(
//     "update",
//     "contests/" + category.contestId + "/categories",
//     category,
//     category.id
//   );

// export const updateContest = async (contest: ContestWithId) =>
//   await crudOperation("update", "contests", contest, contest.id);

export const updateParticipant = async (participant: ParticipantWithId, userId: string) =>
  await crudOperation("update", "users/" + userId + "/participants", participant, participant.id);

export const updatePresentation = async (
  presentation: PresentationWithId,
  applicationId: string,
) => {
  await crudOperation(
    "update",
    "users/" + presentation.userId + "/applications/" + applicationId + "/presentations",
    convertDatesToTimestamps(presentation),
    presentation.id,
  );
};

export const updatePresentationAndParticipantsCounter = async (
  presentation: PresentationWithId,
  application: ApplicationWithId,
) => {
  const operations = [
    (batch: WriteBatch) => {
      const presColRef = col(
        db,
        `users/${presentation.userId}/applications/${application.id}/presentations`,
      );
      const presentationRef = doc(presColRef, presentation.id);
      batch.update(presentationRef, convertDatesToTimestamps(presentation));
    },
    (batch: WriteBatch) => {
      updateParticipantPresentationsMap(batch, db, application.userId, application);
    },
  ];

  await batchOperation(operations);
};

export const updatePresentations = async (presentations: PresentationWithId[]) => {
  const operations = presentations.map((presentation) => (batch: WriteBatch) => {
    const presColRef = col(
      db,
      `users/${presentation.userId}/applications/${presentation.applicationId}/presentations`,
    );
    const presentationRef = doc(presColRef, presentation.id);
    batch.update(presentationRef, convertDatesToTimestamps(presentation));
  });

  await batchOperation(operations);
};

export const updateUser = async (user: AppUser) =>
  await crudOperation("update", "users", user, user.id);

// ----------------------------------------------------- //
// --------- TRANSAKCYJNE AKCEPTOWANIE ZGŁOSZENIA ------ //
// ----------------------------------------------------- //

/**
 * Zatwierdza zgłoszenie tylko jeśli rejestracja jest wciąż otwarta (walidacja w transakcji).
 * Zabezpiecza przed sytuacją, w której UI ma stary kontekst turnieju.
 *
 * @returns { success: boolean, error?: string }
 */
export const approveApplicationIfRegistrationOpen = async (
  userId: string,
  applicationId: string,
  contestId: string,
): Promise<{ success: boolean; error?: string }> => {
  try {
    const contestRef = doc(db, "contests", contestId);
    const appRef = doc(db, `users/${userId}/applications`, applicationId);

    await runTransaction(db, async (tx) => {
      const contestSnap = await tx.get(contestRef);
      if (!contestSnap.exists()) {
        throw new Error("CONTEST_NOT_FOUND");
      }
      const contest = contestSnap.data() as any;

      // Normalizuj registrationEnd do Date i użyj wspólnego helpera isRegistrationOpen
      const registrationEndRaw = contest.registrationEnd;
      const normalizedRegistrationEnd =
        registrationEndRaw instanceof Timestamp
          ? registrationEndRaw.toDate()
          : registrationEndRaw
          ? new Date(registrationEndRaw)
          : null;
      const normalizedContest = { ...contest, registrationEnd: normalizedRegistrationEnd };
      const open = isRegistrationOpen(normalizedContest);
      if (!open) {
        throw new Error("REGISTRATION_CLOSED");
      }

      // Sprawdź bieżący status zgłoszenia
      const appSnap = await tx.get(appRef);
      if (!appSnap.exists()) {
        throw new Error("APPLICATION_NOT_FOUND");
      }
      const application = appSnap.data() as any;
      if (application.status !== "DRAFT") {
        throw new Error("INVALID_STATUS");
      }

      // Aktualizacja statusu w tej samej transakcji
      tx.update(appRef, {
        status: "SENT",
        sendTimestamp: serverTimestamp(),
      });
    });

    return { success: true };
  } catch (e: any) {
    const code = e?.message || "UNKNOWN_ERROR";
    let message = "Nie można zatwierdzić zgłoszenia.";
    if (code === "REGISTRATION_CLOSED") {
      message = "Rejestracja została zamknięta. Nie można wysłać zgłoszenia.";
    } else if (code === "INVALID_STATUS") {
      message = "Zgłoszenie nie jest w trybie edycji.";
    } else if (code === "CONTEST_NOT_FOUND") {
      message = "Nie znaleziono turnieju.";
    } else if (code === "APPLICATION_NOT_FOUND") {
      message = "Nie znaleziono zgłoszenia.";
    }
    return { success: false, error: message };
  }
};

// ----------------------------------------------------- //
// --------------- FUNKCJE GRUP ------------------------ //
// ----------------------------------------------------- //

/**
 * Tworzenie nowej grupy
 *
 * @param group - Dane grupy do utworzenia
 * @param userId - ID użytkownika (właściciela grupy)
 * @returns Promise<GroupWithId> - Utworzona grupa z ID
 */
export const createGroup = async (group: Group, userId: string): Promise<GroupWithId> => {
  return await crudOperation("create", `users/${userId}/groups`, group);
};

/**
 * Pobieranie wszystkich grup użytkownika
 *
 * @param userId - ID użytkownika
 * @returns Promise<GroupWithId[]> - Lista grup posortowana alfabetycznie po nazwie
 */
export const findGroups = async (userId: string): Promise<GroupWithId[]> => {
  return await find(
    "collection",
    `users/${userId}/groups`,
    [],
    [{ field: "name", direction: "asc" }],
  );
};

/**
 * Pobieranie grupy po ID
 *
 * @param userId - ID użytkownika
 * @param groupId - ID grupy
 * @returns Promise<GroupWithId> - Grupa
 */
export const findGroupById = async (userId: string, groupId: string): Promise<GroupWithId> => {
  return await findById(`users/${userId}/groups`, groupId);
};

/**
 * Aktualizacja grupy
 *
 * @param group - Zaktualizowane dane grupy
 * @param userId - ID użytkownika
 * @returns Promise<void>
 */
export const updateGroup = async (group: GroupWithId, userId: string): Promise<void> => {
  await crudOperation("update", `users/${userId}/groups`, group, group.id);
};

/**
 * Usuwanie grupy
 *
 * @param groupId - ID grupy do usunięcia
 * @param userId - ID użytkownika
 * @returns Promise<void>
 */
export const deleteGroup = async (groupId: string, userId: string): Promise<void> => {
  return await crudOperation("delete", `users/${userId}/groups`, undefined, groupId);
};

// ----------------------------------------------------- //
// --------------- FUNKCJE LOGOWANIA ------------------- //
// ----------------------------------------------------- //

/**
 * Tworzenie logu w kolekcji 'logs'
 *
 * @param logData - Dane logu do zapisania
 * @returns Promise<LogEntry> - Utworzony log z ID
 */
export const createLog = async (logData: LogData): Promise<LogEntry> => {
  const logEntry: LogEntry = {
    ...logData,
    id: "", // Będzie wygenerowane przez Firestore
    timestamp: new Date(),
  };

  return await crudOperation("create", "logs", logEntry);
};

/**
 * Pobieranie logów z kolekcji 'logs' z warunkami
 *
 * @param whereConditions - Warunki filtrowania
 * @param orderByConditions - Warunki sortowania
 * @returns Promise<LogEntry[]> - Lista logów
 */
export const findLogsByQuery = async (
  whereConditions: WhereCondition[] = [],
  orderByConditions: OrderByCondition[] = [],
): Promise<LogEntry[]> => {
  return await find("collection", "logs", whereConditions, orderByConditions);
};

/**
 * Pobieranie logu po ID
 *
 * @param logId - ID logu
 * @returns Promise<LogEntry> - Log
 */
export const findLogById = async (logId: string): Promise<LogEntry> => {
  return await findById("logs", logId);
};
